import { baseQuery } from "./base-query";
import { createApi } from "@reduxjs/toolkit/query/react";

export const validationCheckDefinitionsApi = createApi({
  reducerPath: "validationCheckDefinitionsApi",
  baseQuery,
  tagTypes: ["ValidationCheckDefinitions"],
  endpoints: (builder) => ({
    getValidationCheckDefinitions: builder.query<
      TValidationCheckDefinition[],
      void
    >({
      query: () => ({
        document: `
          query GetValidationCheckDefinitions {
            validationCheckDefinitions {
              _id
              checkId
              checkName
              checkType
              enabled
              targetSlotId
              condition
              items { itemId question crossRefSlots enabled }
              description
              requiredSlots
              promptTemplate
              isCustom
              createdAt
              updatedAt
            }
          }
        `,
      }),
      transformResponse: (response: {
        validationCheckDefinitions: TValidationCheckDefinition[];
      }) => response.validationCheckDefinitions,
      providesTags: ["ValidationCheckDefinitions"],
    }),

    createValidationCheck: builder.mutation<
      TValidationCheckDefinition,
      {
        input: {
          checkId: string;
          checkName: string;
          checkType: string;
          targetSlotId?: string;
          condition?: string;
          items?: {
            itemId: string;
            question: string;
            crossRefSlots?: string[];
            enabled?: boolean;
          }[];
          description?: string;
          requiredSlots?: string[];
          promptTemplate: string;
        };
      }
    >({
      query: (variables) => ({
        document: `
          mutation CreateValidationCheck($input: CreateValidationCheckInput!) {
            createValidationCheck(input: $input) {
              _id checkId checkName checkType enabled isCustom
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: {
        createValidationCheck: TValidationCheckDefinition;
      }) => response.createValidationCheck,
      invalidatesTags: ["ValidationCheckDefinitions"],
    }),

    updateValidationCheck: builder.mutation<
      TValidationCheckDefinition,
      {
        checkId: string;
        input: {
          checkName?: string;
          targetSlotId?: string;
          condition?: string;
          items?: {
            itemId: string;
            question: string;
            crossRefSlots?: string[];
            enabled?: boolean;
          }[];
          description?: string;
          requiredSlots?: string[];
          promptTemplate?: string;
          enabled?: boolean;
        };
      }
    >({
      query: (variables) => ({
        document: `
          mutation UpdateValidationCheck($checkId: String!, $input: UpdateValidationCheckInput!) {
            updateValidationCheck(checkId: $checkId, input: $input) {
              _id checkId checkName checkType enabled
              items { itemId question crossRefSlots enabled }
              description requiredSlots promptTemplate isCustom
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: {
        updateValidationCheck: TValidationCheckDefinition;
      }) => response.updateValidationCheck,
      invalidatesTags: ["ValidationCheckDefinitions"],
    }),

    toggleValidationCheck: builder.mutation<
      TValidationCheckDefinition,
      { checkId: string; enabled: boolean }
    >({
      query: (variables) => ({
        document: `
          mutation ToggleValidationCheck($checkId: String!, $enabled: Boolean!) {
            toggleValidationCheck(checkId: $checkId, enabled: $enabled) {
              _id checkId enabled
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: {
        toggleValidationCheck: TValidationCheckDefinition;
      }) => response.toggleValidationCheck,
      invalidatesTags: ["ValidationCheckDefinitions"],
    }),

    toggleValidationCheckItem: builder.mutation<
      TValidationCheckDefinition,
      { checkId: string; itemId: string; enabled: boolean }
    >({
      query: (variables) => ({
        document: `
          mutation ToggleValidationCheckItem($checkId: String!, $itemId: String!, $enabled: Boolean!) {
            toggleValidationCheckItem(checkId: $checkId, itemId: $itemId, enabled: $enabled) {
              _id checkId items { itemId question enabled }
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: {
        toggleValidationCheckItem: TValidationCheckDefinition;
      }) => response.toggleValidationCheckItem,
      invalidatesTags: ["ValidationCheckDefinitions"],
    }),

    deleteValidationCheck: builder.mutation<boolean, { checkId: string }>({
      query: (variables) => ({
        document: `
          mutation DeleteValidationCheck($checkId: String!) {
            deleteValidationCheck(checkId: $checkId)
          }
        `,
        variables,
      }),
      transformResponse: (response: { deleteValidationCheck: boolean }) =>
        response.deleteValidationCheck,
      invalidatesTags: ["ValidationCheckDefinitions"],
    }),
  }),
});

export const {
  useGetValidationCheckDefinitionsQuery,
  useCreateValidationCheckMutation,
  useUpdateValidationCheckMutation,
  useToggleValidationCheckMutation,
  useToggleValidationCheckItemMutation,
  useDeleteValidationCheckMutation,
} = validationCheckDefinitionsApi;
